import { useMemo, useRef, useState } from 'react';
import { BarcodeScannerDialog } from '@/components/barcode';
import { ProductThumb } from '@/components/products/ProductThumb';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { playBarcodeScanBeep } from '@/lib/barcodeScan';
import { filterProductsByQuery, matchProductByBarcode } from '@/lib/productBarcode';
import { productThumbnailUrl } from '@/lib/productImage';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, ScanLine } from 'lucide-react';
import { ProductStatusBadge } from '@/components/products/MarginBadge';

export type ProductSelectStatus = 'ACTIVE' | 'DORMANT' | 'DELISTED' | 'UNMANAGED';

export interface ProductSelectOption {
  id: string;
  sku: string;
  name: string;
  barcode?: string | null;
  unitBarcode?: string | null;
  caseBarcode?: string | null;
  unitsPerCase?: number | null;
  thumbnailUrl?: string | null;
  imageUrl?: string | null;
  media?: Array<{ url: string; mediaType: string }> | null;
  status?: ProductSelectStatus;
}

export function isProductSelectable(product: ProductSelectOption): boolean {
  return product.status !== 'DORMANT' && product.status !== 'DELISTED' && product.status !== 'UNMANAGED';
}

export interface ProductSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  products: ProductSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  triggerClassName?: string;
  contentClassName?: string;
  onBarcodeMatch?: (product: ProductSelectOption) => void;
  /** Dormant/delisted rows stay visible; click this instead of assigning. */
  onUnavailableSelect?: (product: ProductSelectOption) => void;
  /** When true, user can clear selection (value "") — useful for filter UIs. */
  allowEmpty?: boolean;
  emptyLabel?: string;
}

function ProductSelectLabel({ product }: { product: ProductSelectOption }) {
  return (
    <span className="block min-w-0 truncate">
      {product.sku} – {product.name}
    </span>
  );
}

function ProductBarcodeHint({ product }: { product: ProductSelectOption }) {
  const code = product.unitBarcode?.trim() || product.barcode?.trim() || product.caseBarcode?.trim();
  if (!code) return null;
  return <span className="text-xs text-muted-foreground font-mono">{code}</span>;
}

function ProductSelectThumb({
  product,
  size = 'sm',
}: {
  product: ProductSelectOption;
  size?: 'sm' | 'md';
}) {
  const dim = size === 'md' ? 'h-8 w-8' : 'h-7 w-7';
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded',
        dim
      )}
    >
      <ProductThumb imageUrl={productThumbnailUrl(product)} popOut={false} />
    </div>
  );
}

export function ProductSelect({
  value,
  onValueChange,
  products,
  placeholder = 'Search product, SKU, or barcode…',
  disabled,
  id,
  triggerClassName,
  contentClassName,
  onBarcodeMatch,
  onUnavailableSelect,
  allowEmpty = false,
  emptyLabel = 'All products',
}: ProductSelectProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>();

  const selected = value ? products.find((p) => p.id === value) : undefined;
  const filteredProducts = useMemo(
    () => filterProductsByQuery(products, search),
    [products, search]
  );

  const clearSelection = () => {
    onValueChange('');
    setOpen(false);
    setSearch('');
  };

  const handleOpenChange = (next: boolean) => {
    if (next && triggerRef.current) {
      setPopoverWidth(triggerRef.current.getBoundingClientRect().width);
    }
    setOpen(next);
    if (!next) setSearch('');
  };

  const selectProduct = (product: ProductSelectOption, options?: { beep?: boolean }) => {
    setOpen(false);
    setSearch('');
    if (!isProductSelectable(product)) {
      onUnavailableSelect?.(product);
      return;
    }
    window.setTimeout(() => {
      onValueChange(product.id);
      if (options?.beep) playBarcodeScanBeep();
      onBarcodeMatch?.(product);
    }, 0);
  };

  const handleSearchChange = (next: string) => {
    setSearch(next);
    const exact = matchProductByBarcode(products, next);
    if (exact) {
      selectProduct(exact, { beep: true });
    }
  };

  const handleScan = (scanned: string) => {
    setSearch(scanned);
    const match = matchProductByBarcode(products, scanned);
    if (match) {
      selectProduct(match, { beep: false });
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <div className={cn('flex w-full min-w-0 max-w-full', triggerClassName)}>
        <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
          <PopoverTrigger asChild>
            <Button
              ref={triggerRef}
              id={id}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'h-10 min-w-0 flex-1 justify-between overflow-hidden rounded-r-none border-r-0 px-3 font-normal',
                !selected && 'text-muted-foreground'
              )}
            >
            <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left">
              {selected ? (
                <>
                  <ProductSelectThumb product={selected} />
                  <ProductSelectLabel product={selected} />
                </>
              ) : (
                <span className="truncate">{allowEmpty ? emptyLabel : placeholder}</span>
              )}
            </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            style={popoverWidth != null ? { width: popoverWidth } : undefined}
            className={cn('p-0', contentClassName)}
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                value={search}
                onValueChange={handleSearchChange}
                placeholder="Name, SKU, or barcode…"
              />
              <CommandList>
                <CommandEmpty>No products found.</CommandEmpty>
                <CommandGroup>
                  {allowEmpty && (
                    <CommandItem value="__all__" onSelect={clearSelection} className="py-2">
                      <Check
                        className={cn(
                          'mr-1 h-4 w-4 shrink-0',
                          !value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="text-muted-foreground">{emptyLabel}</span>
                    </CommandItem>
                  )}
                  {filteredProducts.map((product) => {
                    const selectable = isProductSelectable(product);
                    const statusHint =
                      product.status === 'DORMANT'
                        ? 'Dormant — open to change status'
                        : product.status === 'DELISTED'
                          ? 'Delisted — open to change status'
                          : product.status === 'UNMANAGED'
                            ? 'Unmanaged — hidden from Express until a Cloud status is set'
                            : undefined;
                    return (
                    <CommandItem
                      key={product.id}
                      value={product.id}
                      title={statusHint}
                      onSelect={() => selectProduct(product)}
                      className={cn('items-center py-1.5', !selectable && 'opacity-70')}
                    >
                      <Check
                        className={cn(
                          'mr-1 h-4 w-4 shrink-0',
                          value === product.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <ProductSelectThumb product={product} size="md" />
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <ProductSelectLabel product={product} />
                        <ProductBarcodeHint product={product} />
                      </div>
                      {product.status && product.status !== 'ACTIVE' && (
                        <ProductStatusBadge status={product.status} className="shrink-0" />
                      )}
                    </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          className="h-10 shrink-0 rounded-l-none px-3 text-muted-foreground hover:text-foreground"
          aria-label="Scan product barcode"
          onClick={() => setScannerOpen(true)}
        >
          <ScanLine className="h-4 w-4" />
        </Button>
      </div>

      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        title="Scan product barcode"
        description="Scan the unit (EAN) or case (ITF) barcode to find the product."
        onScan={handleScan}
      />
    </>
  );
}
